<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core"
import { TuPlanSelectionCard, ButtonSeverity } from "@tinyurldev/tu-ui-vue"
import type { SelectablePlan, TuPlanSelectionCardProps } from "@tinyurldev/tu-ui-vue"

import useTranslation from "@Composables/useTranslation"
import type { Plan, PlanFact } from "@Modules/subscriptions/Data/Models/PlanModel"
import type { Subscription } from "@Modules/subscriptions/Data/Models/SubscriptionModel"
import type { BillingPeriod } from "@Modules/subscriptions/Api/SubscriptionsApi"

//
// Types
// Card model bound onto <TuPlanSelectionCard> via v-bind. Derived from the child's real
// props so the binding is checked against its contract. The suffix-text props are passed
// separately in the template; `id` is a local key/identifier used only by this component.
type SelectableCard = Omit<
  TuPlanSelectionCardProps,
  "monthlyPriceSuffixText" | "yearlyPriceSuffixText"
> & {
  id: string
}

//
// Setup
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5)
const { __ } = useTranslation()

//
// Props
interface Props {
  plans: Plan[]
  currentSubscription: Subscription | null
  billingPeriod: BillingPeriod
  loading: boolean
  disabled: boolean
}
const props = defineProps<Props>()

//
// Emit
interface Events {
  (e: "select-plan", planName: string): void
}
const emit = defineEmits<Events>()

//
// Data
const selectedPlanByCard = ref<Record<string, string>>({})
const enterpriseFacts: PlanFact[] = [
  { content: __("pricing_page.find_plan_section.enterprise_card.facts.links") },
  { content: __("pricing_page.find_plan_section.enterprise_card.facts.tracked_clicks") },
  { content: __("pricing_page.find_plan_section.enterprise_card.facts.branded_domains") },
  { content: __("pricing_page.find_plan_section.enterprise_card.facts.uptime") },
  { content: __("pricing_page.find_plan_section.enterprise_card.facts.compliance") },
]
const volumeLabels = {
  standard: __("pricing_page.find_plan_section.volume_labels.select_link_volume"),
  compact: __("pricing_page.find_plan_section.volume_labels.links_per_month"),
}

//
// Computed
const isMobileView = smallerOrEqual("xl")

// Group plans by their group field
const plansByGroup = computed(() => {
  const groups: Record<string, Plan[]> = {}

  for (const plan of props.plans) {
    if (plan.enterprise || plan.name === "free") {
      continue
    }

    const group = plan.group || "other"
    if (!groups[group]) {
      groups[group] = []
    }
    groups[group].push(plan)
  }

  // Sort each group by price (lowest first)
  for (const plans of Object.values(groups)) {
    plans.sort((a, b) => {
      const priceA = a.getPrice(props.billingPeriod)?.amount ?? 0
      const priceB = b.getPrice(props.billingPeriod)?.amount ?? 0
      return priceA - priceB
    })
  }

  return groups
})

const enterprisePlan = computed(() => {
  return props.plans.find((plan) => plan.enterprise)
})

// Build primary cards (pro and bulk) in order
const primarySelectionCards = computed<SelectableCard[]>(() => {
  return ["pro", "bulk"]
    .map((groupId) => {
      const plans = plansByGroup.value[groupId]
      if (!plans || plans.length === 0) {
        return null
      }
      return buildSelectionCard(groupId, plans)
    })
    .filter((card): card is SelectableCard => card !== null)
})

// Build cards for any other groups
const extraSelectionCards = computed<SelectableCard[]>(() => {
  const cards: SelectableCard[] = []
  for (const [group, plans] of Object.entries(plansByGroup.value)) {
    if (group !== "pro" && group !== "bulk") {
      cards.push(buildSelectionCard(group, plans))
    }
  }
  return cards
})

//
// Methods

function buildSelectionCard(groupId: string, plans: Plan[]): SelectableCard {
  const selectablePlans: SelectablePlan[] = plans.map((plan) => buildSelectablePlan(plan))

  // Get the currently selected plan for this card, or default to first
  const selectedPlanId = selectedPlanByCard.value[groupId] || selectablePlans[0]?.id || ""
  const isCurrent = isCurrentPlanByName(selectedPlanId)

  const card: SelectableCard = {
    id: groupId,
    plans: selectablePlans,
    initialPlan: selectedPlanId,
    volumeLabels,
  }

  if (isCurrent) {
    card.highlightLabel = __("pricing_page.highlight_label.subscribed")
  } else {
    const hasSubscription = props.currentSubscription !== null && !props.currentSubscription.isFree()
    card.actionButton = {
      label: hasSubscription
        ? __("pricing_page.button_label.switch")
        : __("pricing_page.button_label.subscribe"),
      severity: ButtonSeverity.SECONDARY,
      isDisabled: props.disabled,
      isLoading: props.loading,
    }
  }

  return card
}

function buildSelectablePlan(plan: Plan): SelectablePlan {
  const selectablePlan: SelectablePlan = {
    id: plan.name,
    title: plan.formattedTitle(),
    facts: plan.getFacts(),
    monthlyPrice:
      props.billingPeriod === "yearly"
        ? plan.formattedMonthlyEquivalentOfYearlyPrice()
        : plan.formattedMonthlyPrice(),
    yearlyPrice: props.billingPeriod === "yearly" ? plan.formattedYearlyPrice() : undefined,
  }

  const shortVolume = plan.formattedShortVolume()
  if (shortVolume !== "") {
    selectablePlan.shortVolume = shortVolume
  }

  const longVolume = plan.formattedLongVolume()
  if (longVolume !== "") {
    selectablePlan.longVolume = longVolume
  }

  return selectablePlan
}

function isCurrentPlanByName(planName: string): boolean {
  if (!props.currentSubscription) {
    return planName === "free"
  }
  return planName === props.currentSubscription.name
}

function onSelectionPlanChange(cardId: string, planId: string): void {
  selectedPlanByCard.value[cardId] = planId
}

function onPlanSelected(planId: string): void {
  emit("select-plan", planId)
}

//
// Watchers
watch(
  () => props.plans,
  () => {
    // Reset selections when plans change
    selectedPlanByCard.value = {}
  },
)

//
// Exposed Test Vars
defineExpose({
  isCurrentPlanByName,
  onPlanSelected,
  onSelectionPlanChange,
  selectedPlanByCard,
  primarySelectionCards,
  enterprisePlan,
})
</script>

<template>
  <div
    v-if="plans.length > 0"
    class="org-plans-selection"
    :class="{ 'org-plans-selection--mobile': isMobileView }"
  >
    <TuPlanSelectionCard
      v-for="card in primarySelectionCards"
      :key="card.id"
      v-bind="card"
      monthly-price-suffix-text="/ mo *"
      yearly-price-suffix-text="/ yr"
      :class="`_${card.id}_card`"
      @selection-plan-change="(id) => onSelectionPlanChange(card.id, id)"
      @plan-select="onPlanSelected"
    />
    <TuPlanSelectionCard
      v-if="enterprisePlan"
      id="enterprise"
      :plans="[{
        id: 'enterprise',
        title: __('subscriptions:enterprise.title'),
        customTitle: __('pricing_page.find_plan_section.enterprise_card.custom_title'),
        facts: enterpriseFacts,
        yearlyPrice: __('pricing_page.find_plan_section.enterprise_card.yearly_price'),
      }]"
      initial-plan="enterprise"
      :action-button="{
        label: __('pricing_page.button_label.contact_sales'),
        severity: ButtonSeverity.SECONDARY,
        isDisabled: false,
        isLoading: false,
      }"
      :highlight-label="isCurrentPlanByName('enterprise') ? __('pricing_page.highlight_label.subscribed') : undefined"
      monthly-price-suffix-text="/ mo *"
      yearly-price-suffix-text="/ yr"
      class="_enterprise_card"
      @plan-select="onPlanSelected"
    />
    <TuPlanSelectionCard
      v-for="card in extraSelectionCards"
      :key="card.id"
      v-bind="card"
      monthly-price-suffix-text="/ mo *"
      yearly-price-suffix-text="/ yr"
      :class="`_${card.id}_card`"
      @selection-plan-change="(id) => onSelectionPlanChange(card.id, id)"
      @plan-select="onPlanSelected"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.org-plans-selection {
  display: flex;
  gap: spacing.$xxs;
  flex: 3;

  &--mobile {
    flex-direction: column;
  }
}
</style>
